import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  addUserClassifier,
  deleteUserClassifier,
  getHistoricUserClassifier,
  getUserClassifier,
} from '../../services/api';

export interface UserClassifierState {
  userClassifierData: {
    data: any;
    status: null | 'idle' | 'loading' | 'failed';
  };
  historicUserClassifierData: {
    data: any;
    pagination: {
      total: number;
      page: number;
    };
    status: null | 'idle' | 'loading' | 'failed';
  };
  userClassifierDataNormal: {
    data: any;
  };
  userClassifierDataEdited: {
    data: any;
  };
}

const initialState: UserClassifierState = {
  userClassifierData: {
    data: [],
    status: null,
  },
  historicUserClassifierData: {
    data: [],
    pagination: {
      total: 0,
      page: 1,
    },
    status: null,
  },
  userClassifierDataNormal: {
    data: [],
  },
  userClassifierDataEdited: {
    data: [],
  },
};

export const getUserClassifierAsync: any | null = createAsyncThunk(
  'userClassifier/getUserClassifier',
  async (data: any) => {
    const response = await getUserClassifier(
      { userId: data.userId },
      { classifierTypeId: data.classifierTypeId },
    );
    return response;
  },
);

export const getHistoricUserClassifierAsync: any | null = createAsyncThunk(
  'userClassifier/getHistoricUserClassifier',
  async (data: any) => {
    const response = await getHistoricUserClassifier(
      { userId: data.userId },
      {
        classifierTypeId: data.classifierTypeId,
        init: data.init,
        size: data.size,
      },
    );
    return response;
  },
);

export const addUserClassifierAsync: any | null = createAsyncThunk(
  'userClassifier/addUserClassifier',
  async (data: any, { dispatch }) => {
    const response = await addUserClassifier({
      userId: data.userId,
      classifierId: data.classifierId,
    });

    if (response?.data.success) {
      const request = {
        userId: data.userId,
        classifierTypeId: data.extra.classifier.classifierType.id,
      };

      const request2 = {
        userId: data.userId,
        classifierTypeId: data.extra.classifier.classifierType.id,
        init: 0,
        size: 10
      };

      dispatch(getUserClassifierAsync(request));
      dispatch(getHistoricUserClassifierAsync(request2));
    }

    return response;
  },
);

export const deleteUserClassifierAsync: any | null = createAsyncThunk(
  'userClassifier/deleteUserClassifier',
  async (data: any, { dispatch }) => {
    const response = await deleteUserClassifier({
      userId: data.userId,
      userClassifierId: data.userClassifierId,
    });

    if (response?.data.success) {
      const request = {
        userId: data.extra.userClassifier.userId,
        classifierTypeId: data.extra.classifier.classifierType.id
      };

      const request2 = {
        userId: data.userId,
        classifierTypeId: data.extra.classifier.classifierType.id,
        init: 0,
        size: 10
      };

      dispatch(getUserClassifierAsync(request));
      dispatch(getHistoricUserClassifierAsync(request2));
    }
  },
);

export const userClassifierSlice = createSlice({
  name: 'userClassifier',
  initialState,
  reducers: {
    setUserClassifierNormal: (state, { payload }: PayloadAction<any>) => {
      state.userClassifierDataNormal.data = payload;
    },
    setUserClassifierEdited: (state, { payload }: PayloadAction<any>) => {
      state.userClassifierDataEdited.data = payload.map((item: any) => ({
        classifier: item.classifier,
        classifierNameEdit: item.classifierName,
        selected: item.selected,
        userClassifier: item.userClassifier,
      }));
    },
    setUserClassifier: (state, { payload }: PayloadAction<any>) => {
      if (payload.selection == 'SINGLE') {

        state.userClassifierDataEdited.data = state.userClassifierDataEdited.data.map((item: any) => ({
          ...item,
          selected: item.classifier.id == payload.row.classifier.id ? true : false,
        }));
      }

      if (payload.selection == 'MULTIPLE') {

        state.userClassifierDataEdited.data = state.userClassifierDataEdited.data.map((item: any) => ({
          ...item,
          selected: item.classifier.id == payload.row.classifier.id ? !item.selected : item.selected,
        }));
      }
    },
    unsetUserClassifier: (state, { payload }: PayloadAction<any>) => {
      state.userClassifierDataEdited.data = state.userClassifierData.data.filter(
        (item: any) => item.id !== payload.row.userClassifier.id,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserClassifierAsync.pending, (state) => {
        state.userClassifierData.status = 'loading';
      })
      .addCase(getUserClassifierAsync.fulfilled, (state, action) => {
        state.userClassifierData.status = 'idle';
        state.userClassifierData.data = action.payload.data.item.items;
      })
      .addCase(getUserClassifierAsync.rejected, (state) => {
        state.userClassifierData.status = 'failed';
      })
      .addCase(getHistoricUserClassifierAsync.pending, (state) => {
        state.historicUserClassifierData.status = 'loading';
      })
      .addCase(getHistoricUserClassifierAsync.fulfilled, (state, action) => {
        state.historicUserClassifierData.status = 'idle';
        state.historicUserClassifierData.data = action.payload.data.item.items.map((item: any) => ({
          ...item,
          classifierName: item.classifier.code,
        }));
        state.historicUserClassifierData.pagination.total = action.payload.data.item.total;
      })
      .addCase(getHistoricUserClassifierAsync.rejected, (state) => {
        state.historicUserClassifierData.status = 'failed';
      });
  },
});

export const { setUserClassifierNormal, setUserClassifierEdited, setUserClassifier, unsetUserClassifier } =
  userClassifierSlice.actions;

export default userClassifierSlice.reducer;
